import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTravelBlocks } from "./useTravelBlocks";
import { sendVendorNotification, sendCustomerNotification, sendAdminNotification } from "@/data/services";
import { format } from "date-fns";

// Function to send customer notifications about booking status changes
async function sendCustomerStatusNotification(
  bookingData: any,
  status: 'accepted' | 'declined',
  declineReason?: string
) {
  if (!bookingData?.customer_email) {
    console.error('Missing customer email, cannot send notification');
    return false;
  }
  
  try {
    console.log(`Sending customer booking ${status} notification to ${bookingData.customer_email}`);
    
    const startDateTime = new Date(bookingData.start_time);
    const endDateTime = new Date(bookingData.end_time);
    
    // Send notification with our new service that includes retry logic
    return await sendCustomerNotification('booking', {
      customerName: bookingData.customer_name,
      customerEmail: bookingData.customer_email,
      serviceName: bookingData.service_name || "your booking",
      date: format(startDateTime, "EEEE, MMMM d, yyyy"),
      time: `${format(startDateTime, "HH:mm")} - ${format(endDateTime, "HH:mm")}`,
      location: bookingData.customer_address || null,
      note: bookingData.note || null,
      bookingId: bookingData.id,
      status: status,
      declineReason: declineReason || null
    });
  } catch (error: any) {
    console.error(`Error in sendCustomerStatusNotification:`, error);
    return false;
  }
}

// Function to send admin notifications about booking status changes
async function sendAdminStatusNotification(
  bookingData: any,
  status: 'accepted' | 'declined',
  declineReason?: string
) {
  try {
    console.log(`Sending admin booking ${status} notification`);
    
    // Extract customer and service info
    const customerName = bookingData.customer_name || 'Unknown customer';
    const customerEmail = bookingData.customer_email || 'No email provided';
    const serviceName = bookingData.services?.name || "Unknown service";
    
    // Format date and time for admin notification
    const startDateTime = new Date(bookingData.start_time);
    const endDateTime = new Date(bookingData.end_time);
    const formattedDate = format(startDateTime, "EEEE, MMMM d, yyyy");
    const formattedTime = `${format(startDateTime, "HH:mm")} - ${format(endDateTime, "HH:mm")}`;
    
    // Send admin notification about booking status change
    return await sendAdminNotification('booking', {
      serviceName: serviceName,
      userName: customerName,
      userEmail: customerEmail,
      details: {
        bookingId: bookingData.id,
        date: formattedDate,
        time: formattedTime,
        newStatus: status,
        previousStatus: bookingData.status,
        note: bookingData.note || "No additional notes",
        declineReason: declineReason || null
      }
    });
  } catch (error: any) {
    console.error(`Error in sendAdminStatusNotification:`, error);
    return false;
  }
}

export const useBookingActions = (serviceId: string | undefined, canManageBookings: boolean, fetchBookings: () => Promise<void>) => {
  const { toast } = useToast();
  const { createTravelTimeBlocks } = useTravelBlocks(serviceId);
  const [actionInProgress, setActionInProgress] = useState<number | null>(null);
  const [progressValue, setProgressValue] = useState(0);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  // This function already has the correct signature with an optional declineReason parameter
  const handleBookingAction = async (bookingId: number, status: 'accepted' | 'declined', declineReason?: string) => {
    if (!canManageBookings) {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: "You don't have permission to manage this service's calendar",
      });
      return false;
    }

    // Set the booking ID for which an action is in progress
    setActionInProgress(bookingId);
    setProgressValue(15); // Start with initial progress

    try {
      // Get booking details and service info for the notification
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .select(`
          *,
          services:service_id (
            name
          )
        `)
        .eq('id', bookingId)
        .single();
        
      if (bookingError) throw bookingError;

      setProgressValue(30);

      // Update booking status - this is our core operation
      const updateData: any = { status };
      if (status === 'declined' && declineReason) {
        updateData.decline_reason = declineReason;
      }
      
      const { error } = await supabase
        .from('bookings')
        .update(updateData)
        .eq('id', bookingId);

      if (error) throw error;

      setProgressValue(50);

      // Create travel blocks if booking is accepted (non-critical operation)
      if (status === 'accepted') {
        await createTravelTimeBlocks(bookingId).catch(err => 
          console.error('Error creating travel time blocks:', err)
        );
      }

      setProgressValue(70);

      // Process notifications asynchronously
      await Promise.all([
        // Send notification to vendor about the status change
        serviceId && !isNaN(parseInt(serviceId)) ? (async () => {
          const serviceIdNum = parseInt(serviceId);
          const startDateTime = new Date(bookingData.start_time);
          const endDateTime = new Date(bookingData.end_time);
          
          const notificationData = {
            bookingId: bookingId,
            customerName: bookingData.customer_name,
            customerEmail: bookingData.customer_email,
            dateTime: format(startDateTime, "yyyy-MM-dd HH:mm"),
            endTime: format(endDateTime, "HH:mm"),
            status: status,
            note: bookingData.note || "No additional notes",
            declineReason: declineReason || null
          };
          
          console.log('Sending vendor notification about status change:', notificationData);
          return sendVendorNotification('booking', serviceIdNum, notificationData);
        })() : Promise.resolve(false),
        
        // Send notification to customer about booking status change
        bookingData ? (async () => {
          // Extract service name
          const serviceName = bookingData.services?.name || "your booking";
          
          // Add service name to booking data
          const enrichedBookingData = {
            ...bookingData,
            service_name: serviceName
          };
          
          console.log('Sending customer notification about status change:', status);
          // Send customer notification with retry logic
          return sendCustomerStatusNotification(enrichedBookingData, status, declineReason);
        })() : Promise.resolve(false),
        
        // Send notification to admin about booking status change
        bookingData ? (async () => {
          console.log('Sending admin notification about status change:', status);
          return sendAdminStatusNotification(bookingData, status, declineReason);
        })() : Promise.resolve(false)
      ]);

      setProgressValue(90);

      // Refresh bookings data
      await fetchBookings();
      
      setProgressValue(100);
      setShowSuccessAnimation(true);
      
      // After all operations are complete, show success toast and reset state
      setTimeout(() => {
        toast({
          title: "Success",
          description: `Booking ${status === 'accepted' ? 'accepted' : 'declined'} successfully`,
          variant: status === 'accepted' ? 'success' : 'default',
        });
        
        setShowSuccessAnimation(false);
        
        // Add a small delay before clearing the in-progress state to allow animations to complete
        setTimeout(() => {
          setActionInProgress(null);
          setProgressValue(0);
        }, 500);
      }, 1500);

      return true;
    } catch (error: any) {
      console.error('Error handling booking action:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to process booking",
      });
      
      // Reset states on error
      setProgressValue(0);
      setActionInProgress(null);
      return false;
    }
  };

  return {
    handleBookingAction,
    actionInProgress,
    progressValue,
    showSuccessAnimation
  };
};